import { Component, ElementRef, HostListener, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormControl } from '@angular/forms';
import { ActivatedRoute, ParamMap, Router } from '@angular/router';
import { PageEvent } from '@angular/material/paginator';
import { Sort } from '@angular/material/sort';
import { MatSidenav } from '@angular/material/sidenav';
import { MatDialog } from '@angular/material/dialog';
import { SelectionModel } from '@angular/cdk/collections';
import { MatSnackBar } from '@angular/material/snack-bar';
import { combineLatest, map, Subject, take, takeUntil, tap } from 'rxjs';
import { EventTypeService } from '../../../services/event-type.service';
import {
  EventPrintAttributeListFilters,
  EventPrintAttributeService,
} from '../../../services/event-print-attribute.service';
import { DivisionService } from '../../../services/division.service';
import { PrintAttributeType } from '../../../models/print-attribute.model';
import { EventPrintAttributeDefinition } from '../../../models/event-print-attribute.model';
import { CreateEventAttributeComponent } from '../create-event-attribute/create-event-attribute.component';
import { AssignPageComponent } from '../assign-page/assign-page.component';
import { ConfirmDialogComponent } from 'src/app/@shared';
import { DEFAULT_SNACKBAR_CONFIG } from 'src/app/@shared/constants/site.constants';
import { PrintAttributeService } from '../../../services/print-attribute.service';
import { EventWorkflowService } from '../../../services/event-workflow.service';
import { AuthorizationService } from 'src/app/@shared/services/authorization.service';

@Component({
  selector: 'app-event-attributes-list',
  templateUrl: './event-attributes-list.component.html',
  styleUrls: ['./event-attributes-list.component.scss'],
})
export class EventAttributesListComponent implements OnInit, OnDestroy {

  @ViewChild(MatSidenav) sidenav!: MatSidenav;
  @ViewChild('filterSidenav', { read: ElementRef }) filterSidenavElement?: ElementRef<HTMLElement>;
  @ViewChild('filterToggleButton', { read: ElementRef }) filterToggleButton?: ElementRef<HTMLElement>;
  private destroy$ = new Subject<void>();

  divisionFilterControl = new FormControl<string[]>([]);
  attributeTypeFilterControl = new FormControl<string[]>([]);
  eventTypeFilterControl = new FormControl<string[]>([]);
  searchControl = new FormControl('');

  divisionOptions: { id: string; name: string }[] = [];
  eventTypeOptions: { id: string; name: string }[] = [];
  eventId = '0';
  eventAttributes: EventPrintAttributeDefinition[] = [];
  selection = new SelectionModel<EventPrintAttributeDefinition>(true, []);
  attributeTypeOptions: { id: string; label: string }[] = [];

  displayedColumns = [
    'select',
    'Name',
    'EventTypeName',
    'PrintAttributeType',
    'Description',
    'Page',
    'Position',
    'Versions',
    'Source',
    'Actions',
  ];

  viewModel$ = combineLatest([
    this.eventPrintAttributeService.eventPrintAttributes$,
    this.eventPrintAttributeService.page$,
    this.eventPrintAttributeService.totalRecords$,
  ]).pipe(
    tap(([attributes, page, totalRecords]) => {
      this.selection.clear();
    }),
    map(([attributes, page, totalRecords]) => ({
      attributes,
      page,
      totalRecords,
    })),
  );
  readOnlyMode = false;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private eventTypeService: EventTypeService<any>,
    private divisionService: DivisionService<any>,
    private eventPrintAttributeService: EventPrintAttributeService,
    private dialog: MatDialog,
    private matSnackBar: MatSnackBar,
    private printAttributeService: PrintAttributeService,
    private ewService: EventWorkflowService,
    private authorizationService: AuthorizationService
  ) {}

  ngOnInit(): void {
    this.resetFilters();
    this.printAttributeService.printAttributeTypes$.pipe(takeUntil(this.destroy$)).subscribe((types) => {
      this.attributeTypeOptions = types;
    });

    if (this.ewService.readOnlyMode != null) {
      this.readOnlyMode = this.ewService.readOnlyMode;
    }
    this.readOnlyMode = !this.authorizationService.checkAccess('edit||offermang');
    if (!this.canEdit()) {
      this.displayedColumns = this.displayedColumns.filter((column) => column !== 'Actions');
    }
    this.eventId = this.route.snapshot.paramMap.get('eventId') ?? '0';
    this.eventPrintAttributeService.setEventId(this.eventId);
    this.rebuildAttributeTypeOptions(this.route.snapshot.queryParamMap);
    this.syncListRouteToState(this.route.snapshot.queryParamMap);

    this.divisionService.divisionsList$
      .pipe(take(1), takeUntil(this.destroy$))
      .subscribe((divisions) => {
        this.divisionOptions = divisions.map((d) => ({
          id: d.Id,
          name: d.DivisionName,
        }));
      });

    this.eventTypeService.eventTypesList$
      .pipe(take(1), takeUntil(this.destroy$))
      .subscribe((types) => {
        this.eventTypeOptions = types.map((t) => ({
          id: t.Id,
          name: t.EventTypeName,
        }));
      });

    this.route.queryParamMap
      .pipe(takeUntil(this.destroy$))
      .subscribe((qp) => this.syncListRouteToState(qp));

    this.viewModel$.pipe(takeUntil(this.destroy$)).subscribe((vm) => {
      this.eventAttributes = vm.attributes;
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
  
resetFilters(): void {
  this.clearSelection();
  this.eventPrintAttributeService.clearFilters();
  this.eventPrintAttributeService.search('');
}
  

  onSearch(event: Event): void {
    this.eventPrintAttributeService.search((event.target as HTMLInputElement).value);
  }

  clearSearch(): void {
    this.searchControl.setValue('');
    this.eventPrintAttributeService.search('');
    this.selection.clear();
  }

  onSort(sortState: Sort): void {
    this.eventPrintAttributeService.sort(sortState);
    this.selection.clear();
  }

  onPage(pageEvent: PageEvent): void {
    this.selection.clear();
    this.eventPrintAttributeService.page(pageEvent);
  }

  addEventAttribute(): void {
    const dialogRef = this.dialog.open(CreateEventAttributeComponent, {
      width: '760px',
      maxWidth: '95vw',
      disableClose: true,
      data: {
        eventId: this.eventId,
      },
    });

    dialogRef.afterClosed().subscribe((shouldReload) => {
      if (shouldReload) {
        this.selection.clear();
        this.eventPrintAttributeService.reload();
      }
    });
  }

  editEventAttribute(record: EventPrintAttributeDefinition): void {
    const eventAttributeId = record.DomainId || record.Id || '';
    if (!eventAttributeId) {
      return;
    }

    const dialogRef = this.dialog.open(CreateEventAttributeComponent, {
      width: '760px',
      maxWidth: '95vw',
      disableClose: true,
      data: {
        eventId: this.eventId,
        eventAttributeId,
      },
    });

    dialogRef.afterClosed().subscribe((shouldReload) => {
      if (shouldReload) {
        this.selection.clear();
        this.eventPrintAttributeService.reload();
      }
    });
  }

  isAllSelected(): boolean {
    const numSelected = this.selection.selected.length;
    const numRows = this.eventAttributes.length;
    return numRows > 0 && numSelected === numRows;
  }

  masterToggle(): void {
    this.isAllSelected()
      ? this.selection.clear()
      : this.eventAttributes.forEach((row) => this.selection.select(row));
  }

  getSelectedSectionRecords(): EventPrintAttributeDefinition[] {
    return this.selection.selected || [];
  }

  clearSelection(): void {
    this.selection.clear();
  }

  deleteSelectedRecords(): void {
    const selectedIds = this.getSelectedSectionRecords().map((record) => record.DomainId || '').filter(Boolean);
    if (!selectedIds.length || this.eventId === '0') {
      return;
    }

    const confirmDialog = this.dialog.open(ConfirmDialogComponent, {
      data: {
        title: 'Confirm Delete',
        message: 'Do you want to remove Event Attribute(s)?',
      },
      disableClose: true,
    });

    confirmDialog.afterClosed().pipe(take(1)).subscribe((result) => {
      if (result !== true) {
        return;
      }

      this.eventPrintAttributeService.deleteEventPrintAttributes(this.eventId, selectedIds).subscribe((res) => {
          console.log('deleteEventPrintAttributes', res);
          this.clearSelection();
          this.eventPrintAttributeService.reload();
          this.matSnackBar.open('Event Attribute(s) deleted', 'OK', DEFAULT_SNACKBAR_CONFIG);
        });
    });
  }

  cloneSelectedRecords(): void {
    const selectedIds = this.getSelectedSectionRecords().map((record) => record.DomainId || '').filter(Boolean);
    if (!selectedIds.length || this.eventId === '0') {
      return;
    }

    this.eventPrintAttributeService
      .cloneEventPrintAttributes(this.eventId, selectedIds)
      .pipe(take(1))
      .subscribe(() => {
        this.clearSelection();
        this.eventPrintAttributeService.reload();
        this.matSnackBar.open('Event Attribute(s) cloned', 'OK', DEFAULT_SNACKBAR_CONFIG);
      });
  }

  assignPageToSelectedRecords(): void {
    const selectedRecords = this.getSelectedSectionRecords();
    if (!selectedRecords.length || this.eventId === '0') {
      return;
    }

    const dialogRef = this.dialog.open(AssignPageComponent, {
      maxWidth: '95vw',
      disableClose: true,
      data: {
        eventId: this.eventId,
        selectedRecords,
      },
    });

    dialogRef.afterClosed().pipe(take(1)).subscribe((shouldReload) => {
      if (shouldReload) {
        this.clearSelection();
        this.eventPrintAttributeService.reload();
      }
    });
  }

  downloadCSV(): void {
    this.eventPrintAttributeService.downloadEventPrintAttributesCSV(this.eventId);
  }

  getPrintAttributeTypeLabel(typeId: string): string {
    const type = this.attributeTypeOptions.find(t => t.id === typeId);
    return type ? type.label : typeId;
  }
  
  private readFiltersFromControls(): EventPrintAttributeListFilters {
    return {
      divisionIds: [...(this.divisionFilterControl.value ?? [])],
      attributeTypes: [...(this.attributeTypeFilterControl.value ?? [])],
      eventTypeIds: [...(this.eventTypeFilterControl.value ?? [])],
    };
  }

  private patchFilterControls(f: EventPrintAttributeListFilters): void {
    this.divisionFilterControl.setValue([...f.divisionIds], {
      emitEvent: false,
    });
    this.attributeTypeFilterControl.setValue([...f.attributeTypes], {
      emitEvent: false,
    });
    this.eventTypeFilterControl.setValue([...f.eventTypeIds], {
      emitEvent: false,
    });
  }

  private rebuildAttributeTypeOptions(qp: ParamMap): void {
    const base = Object.values(PrintAttributeType).map((label) => ({
      id: label,
      label,
    }));
    const extraIds = qp
      .getAll('attributeTypes')
      .filter((id) => !base.some((b) => b.id === id));
    const extras = extraIds.map((id) => ({ id, label: id }));
    this.attributeTypeOptions = [...base, ...extras];
  }

  private syncListRouteToState(qp: ParamMap): void {
    this.rebuildAttributeTypeOptions(qp);
    const next: EventPrintAttributeListFilters = {
      divisionIds: qp.getAll('divisionIds'),
      attributeTypes: qp.getAll('attributeTypes'),
      eventTypeIds: qp.getAll('eventTypeIds'),
    };
    this.patchFilterControls(next);
    this.eventPrintAttributeService.setListFilters(next);
  }

  canEdit(): boolean {
    return !this.readOnlyMode;
  }

}

